import axios from "axios";
import env from "../env";
import { GET_DEVICE_LOGS_FAILURE, GET_DEVICE_LOGS_REQUEST, GET_DEVICE_LOGS_SUCCESS } from "./types";
import { setAlert } from "./alert";

const normalizeHeader = (value) =>
    String(value || "")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");

const splitCsvLine = (line) => {
    const out = [];
    let cur = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i += 1) {
        const ch = line[i];
        if (ch === '"' && line[i + 1] === '"') {
            cur += '"';
            i += 1;
            continue;
        }
        if (ch === '"') {
            inQuotes = !inQuotes;
            cur += ch;
            continue;
        }
        if (ch === "," && !inQuotes) {
            out.push(cur);
            cur = "";
            continue;
        }
        cur += ch;
    }
    out.push(cur);
    return out;
};

const unquoteCsvCell = (value) => {
    const str = String(value ?? "");
    if (str.length >= 2 && str.startsWith('"') && str.endsWith('"')) {
        return str.slice(1, -1).replace(/""/g, '"');
    }
    return str;
};

const escapeCsvCell = (value) => {
    const str = String(value ?? "");
    if (/[",\n]/.test(str)) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
};

const formatUnixToUiDateTime = (rawValue) => {
    const plain = unquoteCsvCell(rawValue).trim();
    const num = Number(plain);
    if (!Number.isFinite(num)) return rawValue;
    const tsMs = num > 1e12 ? num : num * 1000;
    const dt = new Date(tsMs);
    if (Number.isNaN(dt.getTime())) return rawValue;

    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const dd = String(dt.getDate()).padStart(2, "0");
    const mon = months[dt.getMonth()];
    const yyyy = dt.getFullYear();
    const hour24 = dt.getHours();
    const hour12 = hour24 % 12 || 12;
    const hh = String(hour12).padStart(2, "0");
    const mm = String(dt.getMinutes()).padStart(2, "0");
    const mer = hour24 >= 12 ? "PM" : "AM";
    // Keep timestamp as one compact token in CSV text view.
    return `${dd}-${mon}-${yyyy}_${hh}:${mm}${mer}`;
};

// Status export: map UI `createdAt` to whatever header the server CSV used (`ts`, etc.) after normalizeHeader().
const statusCsvKeyAliases = {
    createdat: ["ts", "timestamp"],
};

/** Rebuild CSV rows so only currently visible data columns appear, in UI order, with human-readable createdAt when applicable. */
const filterCsvByVisibleColumns = (
    csvText,
    visibleColumnLabels = [],
    visibleColumns = [],
    visibleColumnDefs = []
) => {
    // Keep only columns currently visible in UI and preserve that same order in export.
    const lines = String(csvText || "").split(/\r?\n/);
    if (!lines.length || !lines[0]) return csvText;

    const headerCells = splitCsvLine(lines[0]);
    const normalizedHeaderToIndex = new Map(
        headerCells.map((cell, idx) => [normalizeHeader(cell), idx])
    );

    // Prefer explicit key+label definitions from UI; fallback to key/label arrays when needed.
    const defs = Array.isArray(visibleColumnDefs) && visibleColumnDefs.length
        ? visibleColumnDefs
        : (Array.isArray(visibleColumns) ? visibleColumns : []).map((key, idx) => ({
            key,
            label: (Array.isArray(visibleColumnLabels) ? visibleColumnLabels[idx] : "") || key,
        }));

    const normalizedActions = normalizeHeader("actions");
    // Resolve UI columns to actual CSV indices, including key alias support (e.g. createdAt -> ts).
    const resolvedColumns = defs
        .map((def) => {
            const keyNorm = normalizeHeader(def?.key);
            const labelNorm = normalizeHeader(def?.label);
            if (!keyNorm && !labelNorm) return null;
            if (keyNorm === normalizedActions || labelNorm === normalizedActions) return null;

            const candidates = [
                keyNorm,
                ...(statusCsvKeyAliases[keyNorm] || []).map(normalizeHeader),
                labelNorm,
            ].filter(Boolean);
            const foundIdx = candidates.find((candidate) => normalizedHeaderToIndex.has(candidate));
            if (foundIdx == null) return null;
            const idx = normalizedHeaderToIndex.get(foundIdx);
            return { idx, label: def?.label || headerCells[idx], key: def?.key };
        })
        .filter(Boolean);

    if (!resolvedColumns.length) return csvText;

    const filteredLines = lines.map((line, lineIndex) => {
        if (line === "") return line;
        if (lineIndex === 0) {
            return resolvedColumns.map((col) => escapeCsvCell(col.label)).join(",");
        }
        const cells = splitCsvLine(line);
        const formatted = resolvedColumns.map((col) => {
            const raw = col.idx < cells.length ? cells[col.idx] : "";
            if (String(col.key || "").toLowerCase() === "createdat") {
                return escapeCsvCell(formatUnixToUiDateTime(raw));
            }
            return escapeCsvCell(unquoteCsvCell(raw));
        });
        return formatted.join(",");
    });

    return filteredLines.join("\n");
};

export const getDeviceDACLogsData = (params, cb = () => { }) => (dispatch) => {
    const { macAddress, ...parameters } = params;
    if (!/^([0-9A-Fa-f]{2}[:]){5}[0-9A-Fa-f]{2}$/.test(macAddress)) {
        dispatch(setAlert(("Please enter a valid MAC Address"), "danger"));
        dispatch({
            type: GET_DEVICE_LOGS_FAILURE,
            payload: [],
        });
        cb();
        return;
    }
    dispatch({
        type: GET_DEVICE_LOGS_REQUEST,
    });
    axios.get(
        `${env.GET_DEVICEMANAGEMENT_API_URL()}/simplifi/v1/certificateManager/dac/${macAddress}/log`, { params: parameters }).then(({ data }) => {
            dispatch({
                type: GET_DEVICE_LOGS_SUCCESS,
                payload: Array?.isArray(data) ? data : [],
            });
            cb();
        }).catch(
            (err) => {
                cb();
                const msg = err?.userMessage ?? err?.response?.data?.message ?? "Failed to fetch device DAC data.";
                dispatch(setAlert(msg, "danger"));
                dispatch({
                    type: GET_DEVICE_LOGS_FAILURE,
                    payload: [],
                });
            })
}

export const getDACCount = (params, cb = () => { }) => (dispatch) => {
    dispatch({
        type: GET_DEVICE_LOGS_REQUEST,
    });
    axios.get(
        `${env.GET_DEVICEMANAGEMENT_API_URL()}/simplifi/v1/certificateManager/dac/count`,
        { params: params }).then(({ data }) => {
            dispatch({
                type: GET_DEVICE_LOGS_SUCCESS,
                payload: data,
            });
            cb();
        }).catch(
            (err) => {
                cb();
                const msg = err?.userMessage ?? err?.response?.data?.message ?? "Failed to fetch device DAC data.";
                dispatch(setAlert(msg, "danger"));
                dispatch({
                    type: GET_DEVICE_LOGS_FAILURE,
                    payload: {},
                });
            })
}

export const getDeviceLogsData = (deviceId) => {
    return new Promise((resolve, reject) => {
        axios
            .get(
                `${env.GET_DEVICEMANAGEMENT_API_URL()}/simplifi/v1/deviceservices/device/${deviceId}/logs`
            )
            .then((res) => resolve(res.data))
            .catch((err) => reject(err));
    });
};

export const getDeviceConnDisconnLogsData = (deviceId, cb = () => { }) => (dispatch) => {
    dispatch({
        type: GET_DEVICE_LOGS_REQUEST,
    });
    axios.get(
        `${env.GET_DEVICEMANAGEMENT_API_URL()}/simplifi/v1/deviceservices/device/${deviceId}/connDisconnLogs`).then(({ data }) => {
            dispatch({
                type: GET_DEVICE_LOGS_SUCCESS,
                payload: Array?.isArray(data) ? data : [],
            });
            cb();
        }).catch(
            (err) => {
                cb();
                dispatch(setAlert((err?.response?.data?.message ?? "Failed to fetch Connection status data.") + "- Device ID: " + deviceId, "danger"));
                dispatch({
                    type: GET_DEVICE_LOGS_FAILURE,
                    payload: [],
                });
            })
}


export const getDeviceDiagnosticsData = (params, cb = () => { }) => (dispatch) => {
    let { deviceId, ...args } = params
    const config = {
        params: args
    };
    dispatch({
        type: GET_DEVICE_LOGS_REQUEST,
    });
    axios.get(
        `${env.GET_DEVICEMANAGEMENT_API_URL()}/simplifi/v1/deviceservices/device/${deviceId}/deviceDiagnosticsLogs`, config).then(({ data }) => {
            dispatch({
                type: GET_DEVICE_LOGS_SUCCESS,
                payload: Array?.isArray(data) ? data : [],
            });
            cb();
        }).catch(
            (err) => {
                const baseMsg = err?.userMessage ?? err?.response?.data?.message ?? "Failed to fetch device diagnostic data.";
                dispatch(setAlert(baseMsg + " - Device ID: " + deviceId, "danger"));
                dispatch({
                    type: GET_DEVICE_LOGS_FAILURE,
                    payload: [],
                });
                cb();
            })
}

export const downloadDeviceDiagnosticReport = (filteredParams, cb = () => { }) => (dispatch) => {
    let { printableStartDate, deviceId, ...params } = filteredParams
    axios
        .get(
            `${env.GET_DEVICEMANAGEMENT_API_URL()}/simplifi/v1/deviceservices/device/${deviceId}/deviceDiagnosticsFile`,
            {
                params: params,
            }
        )
        .then((res) => {
            const blob = new Blob([res.data], { type: "text/csv" });
            let url = window.URL.createObjectURL(blob);
            let a = document.createElement("a");
            a.href = url;
            a.download = `Device_Diagnostic_${deviceId}_${printableStartDate}`;
            a.click();
            cb();
            dispatch(setAlert(("Downloaded device diagnostic data: ") + printableStartDate + " - Device ID: " + deviceId, "success"));
        }).catch((err) => {
            cb();
            dispatch(setAlert((err?.response?.data?.message ?? "Failed to download device diagnostic data") + "- Device ID: " + deviceId, "danger"));
        })
};


/** Fetches raw status CSV, then post-processes columns to match {@link DeviceStatus} visible grid + labels. */
export const downloadDeviceStatusReport = (filteredParams, cb = () => { }) => (dispatch) => {
    let {
        printableStartDate,
        deviceId,
        visibleColumnLabels = [],
        visibleColumns = [],
        visibleColumnDefs = [],
        ...params
    } = filteredParams
    axios
        .get(
            `${env.GET_DEVICEMANAGEMENT_API_URL()}/simplifi/v1/deviceservices/device/${deviceId}/deviceStatusFile`,
            {
                params: params,
            }
        )
        .then((res) => {
            const csvData = filterCsvByVisibleColumns(
                res.data,
                visibleColumnLabels,
                visibleColumns,
                visibleColumnDefs
            );
            const blob = new Blob([csvData], { type: "text/csv" });
            let url = window.URL.createObjectURL(blob);
            let a = document.createElement("a");
            a.href = url;
            a.download = `Device_Status_${deviceId}_${printableStartDate}`;
            a.click();
            cb();
            dispatch(setAlert(("Downloaded device status data: ") + printableStartDate + " - Device ID: " + deviceId, "success"));
        }).catch((err) => {
            cb();
            dispatch(setAlert((err?.response?.data?.message ?? "Failed to download device status data.") + "- Device ID: " + deviceId, "danger"));
        })
};


export const getDeviceStatusData = (params, cb = () => { }) => (dispatch) => {
    let { deviceId, ...args } = params
    const config = {
        params: args
    };
    dispatch({
        type: GET_DEVICE_LOGS_REQUEST,
    });
    axios.get(
        `${env.GET_DEVICEMANAGEMENT_API_URL()}/simplifi/v1/deviceservices/device/${deviceId}/deviceStatusLogs`, config).then(({ data }) => {
            dispatch({
                type: GET_DEVICE_LOGS_SUCCESS,
                payload: Array?.isArray(data) ? data : [],
            });
            cb();
        }).catch(
            (err) => {
                const baseMsg = err?.userMessage ?? err?.response?.data?.message ?? "Failed to fetch device status data.";
                dispatch(setAlert(baseMsg + " - Device ID: " + deviceId, "danger"));
                dispatch({
                    type: GET_DEVICE_LOGS_FAILURE,
                    payload: [],
                });
                cb();
            })
}